import { Document } from '@/interfaces/document';
import { VariableType } from 'json-to-graphql-query';
import { EntityResource } from './entity.resource';
import { DocumentFilterVariables } from './queryVariables/filters/filters';

/**
 * Resource Dokumentu
 *
 * @export
 * @class DocumentResource
 * @extends {EntityResource<Document>}
 */
export class DocumentResource extends EntityResource<Document> {
  public getFields = {
    id: true,
    procedureId: true,
    data: true,
    statusId: true,
    status: { id: true, name: true, color: true, stageId: true, stage: { id: true, module: { id: true, name: true } } },
    procedure: { id: true, name: true, hidden: true, templateId: true, status: { id: true, stage: { id: true } } },
    creator: { id: true, firstName: true, lastName: true },
    creatorId: true,
    systemNumbers: true,
    checksum: true,
    signedAttachment: {
      id: true,
      metadata: true
    },
    parentId: true,
    children: { id: true, data: true, creatorId: true }
  };

  public listFields = {
    id: true,
    procedureId: true,
    procedure: {
      id: true,
      templateId: true,
      name: true
    },
    data: true,
    statusId: true,
    status: { id: true, name: true, color: true, stage: { id: true, module: { id: true, name: true } } }
  };

  protected listVariables = DocumentFilterVariables;

  constructor() {
    super({
      entityName: 'document'
    });
  }

  /**
   * Ustawia nowy status dokumentu
   *
   * @param {number} id
   * @param {number} statusId
   * @returns {Promise<number>}
   * @memberof DocumentResource
   */
  public setStatus(id: number, statusId: number, comment?: string): Promise<number> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: { id: 'Int!', statusId: 'Int!', comment: 'String' },
        documentSetStatus: {
          __args: {
            id: new VariableType('id'),
            statusId: new VariableType('statusId'),
            comment: new VariableType('comment')
          }
        }
      }
    });
    return this.mutation({ mutation, variables: { id, statusId, comment } }, 'documentSetStatus');
  }

  /**
   * Ustawia nowe statusy dokumentów
   *
   * @param {[number]} ids
   * @param {number} statusId
   * @param {string} [comment]
   * @returns {Promise<number>}
   * @memberof DocumentResource
   */
  public setStatuses(ids: [number], statusId: number, comment?: string): Promise<number> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: { ids: '[Int!]!', statusId: 'Int!', comment: 'String' },
        documentSetStatuses: {
          __args: {
            ids: new VariableType('ids'),
            statusId: new VariableType('statusId'),
            comment: new VariableType('comment')
          }
        }
      }
    });
    return this.mutation({ mutation, variables: { ids, statusId, comment } }, 'documentSetStatuses');
  }

  /**
   * Waliduje dane dokumentu
   *
   * @param {*} model
   * @param {string} queryName
   * @returns {Promise<boolean>}
   * @memberof DocumentResource
   */
  public validateData(variables: any, queryName: string): Promise<boolean> {
    const query = this.prepareOptions({
      query: {
        __variables: { id: 'Int!', model: 'DocumentInput' },
        [queryName]: {
          __args: { id: new VariableType('id'), model: new VariableType('model') }
        }
      }
    });
    return this.query({ query, variables }, queryName);
  }

  /**
   * Aktualizuje numery systemowe
   *
   * @param {number} id
   * @param {*} model
   * @returns {Promise<number>}
   * @memberof DocumentResource
   */
  public updateSystemNumbers(id: number, model: any): Promise<number> {
    const mutation = this.prepareOptions({
      mutation: {
        __variables: { id: 'Int!', model: 'DocumentSystemNumbersInput!' },
        documentUpdateSystemNumbers: {
          __args: { id: new VariableType('id'), model: new VariableType('model') }
        }
      }
    });
    return this.mutation({ mutation, variables: { id, model } }, 'documentUpdateSystemNumbers');
  }

  /**
   * Pobranie url do MDOK po UID
   *
   * @param {number} uid
   * @param {number} [documentId]
   * @param {number} [packId]
   * @param {number} [paymentPackId]
   * @returns {Promise<string>}
   * @memberof DocumentResource
   */
  public getMdokUrlByUid(uid: number, documentId?: number, packId?: number, paymentPackId?: number): Promise<string> {
    const query = this.prepareOptions({
      query: {
        __variables: { documentId: 'Int', packId: 'Int', paymentPackId: 'Int', uid: 'Int!' },
        documentMdokUrlByUid: {
          __args: {
            documentId: new VariableType('documentId'),
            packId: new VariableType('packId'),
            paymentPackId: new VariableType('paymentPackId'),
            uid: new VariableType('uid')
          }
        }
      }
    });
    return this.query({ query, variables: { uid, documentId, packId, paymentPackId } }, 'documentMdokUrlByUid');
  }

  /**
   * Pobieranie wartości DocumentValues danego dokumentu
   *
   * @param {number} id
   * @returns {Promise<any>}
   * @memberof DocumentResource
   */
  public getDocumentValues(id: number): Promise<any> {
    const query = this.prepareOptions({
      query: {
        __variables: { id: 'Int!' },
        documentGet: {
          __args: { id: new VariableType('id') },
          id: true,
          documentValues: {
            id: true,
            code: true,
            value: true
          }
        }
      }
    });
    return this.query({ query, variables: { id } }, 'documentGet');
  }
}

export const documentResource = new DocumentResource();
